import type { ReactNode } from 'react';
import { Button } from './Button';
import { Notice } from './Notice';

/**
 * Os sete estados reais de uma tela com dado remoto.
 *
 * O tipo é uma união discriminada, e `AsyncView` não tem caso padrão: se uma
 * tela esquecer um estado, o TypeScript reclama na hora da construção do
 * objeto. É essa a razão de existir do componente — não reuso visual, mas
 * impedir que "carregando" e "erro" continuem sendo tratados como detalhe de
 * quem lembrar.
 *
 *   loading    primeira carga, sem nada em tela ainda
 *   empty      a consulta funcionou e não há o que mostrar
 *   partial    há dado utilizável, porém incompleto ou desatualizado
 *   ready      caso normal
 *   overflow   há mais do que cabe: mostra o recorte e diz quanto falta
 *   error      falhou; oferece nova tentativa, nunca só a mensagem
 *   disabled   indisponível por permissão ou configuração, com o motivo
 */
export type AsyncState<T> =
  | { status: 'loading' }
  | { status: 'empty' }
  | { status: 'partial'; data: T; reason: string; onRetry?: () => void }
  | { status: 'ready'; data: T }
  /**
   * Teto de consulta atingido.
   *
   * A forma anterior pedia `total`, e nenhuma tela conseguia produzi-lo: os
   * endpoints truncados do produto (`LIMIT 200` em agendamentos, `LIMIT 100`
   * em disparos e execuções) devolvem o recorte sem dizer quantos existem.
   * Estado que ninguém consegue produzir é código morto dentro de uma união
   * que existe justamente para obrigar a tratar todos os casos — então o que
   * mudou foi o estado, não a realidade: agora ele carrega o TETO, que é o que
   * de fato se sabe.
   *
   * Sem `onMore`: esses endpoints não têm paginação. Um botão "carregar mais"
   * que não carrega nada seria pior do que o silêncio que existia antes.
   */
  | { status: 'overflow'; data: T; shown: number; teto: number }
  | { status: 'error'; message: string; onRetry: () => void }
  | { status: 'disabled'; reason: string };

export interface AsyncViewProps<T> {
  state: AsyncState<T>;
  children: (data: T) => ReactNode;
  /** Rótulo do que está sendo carregado. Entra na região viva e no esqueleto:
   *  "Carregando conversas" diz mais do que "Carregando". */
  label: string;
  /** Título e texto do vazio. Obrigatórios: um vazio genérico ("Nenhum
   *  resultado") não diz o que fazer, e o vazio é justamente onde a pessoa
   *  mais precisa de direção. */
  empty: { title: string; text: string; action?: ReactNode };
  /** Quantas linhas de esqueleto. Aproxime do tamanho típico da lista. */
  skeletonRows?: number;
}

export function AsyncView<T>({ state, children, label, empty, skeletonRows = 5 }: AsyncViewProps<T>) {
  switch (state.status) {
    case 'loading':
      return (
        <div className="skeleton" role="status" aria-live="polite" aria-busy="true">
          <span className="sr-only">{`Carregando ${label}`}</span>
          {/* A forma do esqueleto imita a da tabela que vai chegar: cabeçalho
              e colunas na mesma proporção. Barras cinza de largura total
              — a primeira versão disto — não reduzem deslocamento nenhum,
              porque o conteúdo real não tem essa forma. */}
          <div className="skeleton__head" aria-hidden="true" />
          {Array.from({ length: skeletonRows }, (_, index) => (
            <div key={index} className="skeleton__row skeleton__cells" aria-hidden="true">
              <span className="skeleton__cell" />
              <span className="skeleton__cell" />
              <span className="skeleton__cell" />
              <span className="skeleton__cell" />
            </div>
          ))}
        </div>
      );

    case 'empty':
      return (
        <div className="state">
          <p className="state__title">{empty.title}</p>
          <p className="state__text">{empty.text}</p>
          {empty.action}
        </div>
      );

    case 'partial':
      return (
        <div>
          <div className="state-bar">
            <span>{state.reason}</span>
            {state.onRetry && (
              <Button variant="secondary" size="sm" onClick={state.onRetry}>Atualizar</Button>
            )}
          </div>
          {children(state.data)}
        </div>
      );

    case 'ready':
      return <>{children(state.data)}</>;

    case 'overflow':
      return (
        <div>
          {children(state.data)}
          <div className="state-overflow" role="status">
            <span>
              {`Mostrando ${state.shown} itens, que é o teto desta consulta. Pode haver mais que o servidor não devolveu.`}
            </span>
          </div>
        </div>
      );

    case 'error':
      return (
        <Notice tone="danger" title="Não foi possível carregar" live>
          <p>{state.message}</p>
          <p style={{ marginBlockStart: 'var(--space-3)' }}>
            <Button variant="secondary" size="sm" onClick={state.onRetry}>Tentar de novo</Button>
          </p>
        </Notice>
      );

    /* Sem permissão ou desligado por configuração. Diz o motivo: uma seção que
       simplesmente some do menu faz a pessoa procurar por uma função que
       existe e ela não pode ver. */
    case 'disabled':
      return (
        <div className="state">
          <p className="state__title">Indisponível</p>
          <p className="state__text">{state.reason}</p>
        </div>
      );
  }
}
